import type React from "react";
import {
  Grid,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import ThumbnailUploader from "../fields/ThumbnailUploader";
import DynamicListField from "../fields/DynamicListFields";
import CustomTextField from "../../../../components/common/Textfield";
import { CourseTabProps } from "../types/course.types";
import PdfUploader from "../fields/PdfUploader";
import DescriptionRichTextEditor from "../../../configurations/components/DescriptionRichTextEditor";

export const SectionTitle = ({ children }: { children: string }) => (
  <Typography
    variant="h6"
    sx={{ fontWeight: 600, mb: 1.5, mt: 3, color: "text.primary" }}
  >
    {children}
  </Typography>
);

const AboutCourseTab: React.FC<CourseTabProps> = ({
  formData,
  onFormChange,
}) => {
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    const slug = title
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]/g, "");
    onFormChange({ title, slug });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <SectionTitle>Basic Information</SectionTitle>

      <CustomTextField
        fullWidth
        label="Course Title"
        placeholder="e.g., Complete Web Development Bootcamp"
        value={formData.title}
        onChange={handleTitleChange}
        variant="outlined"
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Course Type</InputLabel>
            <Select
              value={formData.courseCategory}
              label="Course Type"
              onChange={(e) =>
                onFormChange({
                  courseCategory: e.target.value as "video" | "pdf" | "blog",
                })
              }
            >
              <MenuItem value="video">Video Course</MenuItem>
              <MenuItem value="pdf">PDF Course</MenuItem>
              <MenuItem value="blog">Blog</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) =>
                onFormChange({
                  status: e.target.value as "draft" | "published" | "archived",
                })
              }
              label="Status"
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <SectionTitle>Description</SectionTitle>

      <CustomTextField
        fullWidth
        label="Full Description"
        placeholder="Detailed description of what students will learn..."
        value={formData.fullDescription}
        onChange={(e) => onFormChange({ fullDescription: e.target.value })}
        variant="outlined"
        multiline
        rows={5}
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ThumbnailUploader
            onUpload={(file) => onFormChange({ thumbnailFile: file })}
            previewUrl={formData.thumbnailUrl}
          />
        </Grid>
      </Grid>

      {formData.courseCategory === "video" && (
        <>
          <CustomTextField
            placeholder="Video url"
            value={formData.videoUrls}
            onChange={(e) => onFormChange({ videoUrls: e.target.value })}
            variant="outlined"
          />
        </>
      )}

      {formData.courseCategory === "pdf" && (
        <>
          <PdfUploader
            onUpload={(file) => onFormChange({ pdfFile: file })}
            previewUrl={formData.pdfUrl}
          />
        </>
      )}

      {formData.courseCategory === "blog" && (
        <>
          <SectionTitle>Blog Article</SectionTitle>

          {/* <CustomTextField
            fullWidth
            multiline
            rows={10}
            placeholder="Write your blog article here..."
            value={formData.blogContent}
            onChange={(e) => onFormChange({ blogContent: e.target.value })}
            variant="outlined"
          /> */}

          <DescriptionRichTextEditor
            value={formData.blogContent}
            onChange={(html) => onFormChange({ blogContent: html })}
            placeholder="Write your blog article here..."
          />
        </>
      )}

      <SectionTitle>Skills You Will Gain</SectionTitle>
      <DynamicListField
        items={formData.skills}
        onItemsChange={(items) => onFormChange({ skills: items })}
        placeholder="Add a skill"
      />

      <SectionTitle>Course Requirements</SectionTitle>
      <DynamicListField
        items={formData.requirements}
        onItemsChange={(items) => onFormChange({ requirements: items })}
        placeholder="Add a requirement"
      />
    </Box>
  );
};

export default AboutCourseTab;
